import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Action, Trigger, AgentTool } from '@/types/workflowDefinition';
import type { ValueSegment } from '@/types/valueSegment';
import { ValueSegmentType, generateSegmentId } from '@/types/valueSegment';
import type { NodeSettings, RetryPolicy, RetryPolicyType, RunAfterSettings } from '@/types/nodeSettings';
import { completeRetryPolicy, serializeRetryPolicyForInputs } from '@/types/nodeSettings';
import type { ParameterInfo } from '@/types/operationParameter';
import { serializeSegmentsToValue, parseValueToSegments } from '@/services/tokenParsingService';
import { serializeParameterValue } from '@/services/workflowSerializer';
import { getBuiltInManifest } from '@/data/manifests';
import { requiresConnection } from '@/types/operationManifest';
import { operationRequiresConnection } from '@/types/connectionValidation';
import { telemetryService } from '@/services/telemetryService';
import { wrapAsExpression } from '@/utils/expressions';

// Import parameter utilities from separate module
import {
  extractParameterValues,
  extractPickerDisplayValues,
  serializePickerDisplayValuesToMetadata,
  buildInputsFromParameters,
  isValueSegmentArray,
  deepClone,
  type ParameterValue,
  type SwaggerOperationMetadata,
} from './nodeEditor/parameterUtils';

// Re-export SwaggerOperationMetadata for external use
export type { SwaggerOperationMetadata };

// =============================================================================
// AUTO-SAVE MECHANISM
// =============================================================================

/** Debounce delay for auto-save in milliseconds */
const AUTO_SAVE_DELAY = 500;

/** Timer ID for debounced auto-save */
let autoSaveTimerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Timestamp of the most recent `openFieldFork` call. Used to compute
 * `ms_since_open` for `tokenpicker.fork.tab_switched` telemetry. Lives at
 * module scope so it survives across store actions without polluting state.
 */
let fieldForkOpenedAt: number | null = null;

/**
 * Schedule an auto-save with debouncing.
 * Called internally by parameter/settings setters.
 */
function scheduleAutoSave(applyChangesFn: () => Promise<{ success: boolean }>): void {
  // Clear any pending auto-save
  if (autoSaveTimerId) {
    clearTimeout(autoSaveTimerId);
  }

  autoSaveTimerId = setTimeout(() => {
    autoSaveTimerId = null;
    applyChangesFn().catch((error) => {
      console.error('[nodeEditorStore] Auto-save failed:', error);
    });
  }, AUTO_SAVE_DELAY);
}

// Types
export type NodeType = 'action' | 'trigger' | 'tool';

/** Context for actions inside scope containers (ForEach, Until, etc.) */
export interface ScopeContext {
  /** Name of the immediate parent scope action */
  parentActionName: string;
  /** Type of scope branch (e.g., 'loop') */
  branchType: string;
  /** Chain of parent scopes for nested loops */
  parentScopes?: Array<{ actionName: string; type: string }>;
}

export interface SelectedNode {
  id: string;
  name: string;
  type: NodeType;
  nodeData: Action | Trigger;
  parentAgentName?: string;
  toolName?: string;
  /** Tool metadata when editing a tool - contains description, agentParameterSchema */
  toolData?: AgentTool;
  /** Name of the inner action within a tool */
  innerActionName?: string;
  /** Scope context when action is inside a ForEach or other scope container */
  scopeContext?: ScopeContext;
  /**
   * When true, this `tool` selection represents an MCP server tool that has
   * no inner workflow action. The editor must skip inner-action extraction
   * and render the MCP-specific configuration tab instead.
   */
  isMcpTool?: boolean;
}


// Re-export ParameterValue type from utilities
export type { ParameterValue } from './nodeEditor/parameterUtils';

export type ExpandedTab = 'parameters' | 'connection' | 'settings' | 'code' | 'about';

/**
 * Source of value for a parameter field — drives the FieldFork popover and
 * the rail tabs. `'tokens'` shows the dynamic-content tree; `'expr'` shows
 * the function picker.
 */
export type ValueSource = 'tokens' | 'expr';

/** Anchor rect captured at chip click; FieldFork positions from this. */
export interface FieldForkAnchor {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface FieldForkState {
  open: boolean;
  parameterId: string | null;
  valueSource: ValueSource;
  anchor: FieldForkAnchor | null;
}

const INITIAL_FIELD_FORK_STATE: FieldForkState = {
  open: false,
  parameterId: null,
  valueSource: 'tokens',
  anchor: null,
};

export interface NodeEditorState {
  // Modal state
  isOpen: boolean;
  /** Initial tab to show when opening - used to navigate to specific tab */
  initialTab: ExpandedTab | null;

  /**
   * When true, all user-driven mutations (parameters, settings, rename,
   * connection/operation pickers, auto-save) are blocked at the store
   * level. Synced from the DraftModeContext by the NodeEditor root.
   */
  isReadOnly: boolean;

  // Selected node
  node: SelectedNode | null;

  // Editing state - values can be raw or value segments
  parameterValues: Record<string, ParameterValue>;
  originalValues: Record<string, ParameterValue>;
  isDirty: boolean;
  validationErrors: Record<string, string>;

  /**
   * File/folder picker display values keyed by workflow value. Mirrors
   * LogicAppsUX, which stores `{ [value]: display }` on `action.metadata`
   * so the friendly path the user picked survives a workflow reload.
   * Loaded on editor open from `nodeData.metadata`, written back during
   * serialization, mutated via `setPickerDisplayValue`.
   */
  pickerDisplayValues: Record<string, string>;

  // Swagger metadata state (for ApiConnection serialization)
  // These are populated when parameters are loaded from swagger and used during serialization
  // to construct proper swagger-format inputs (method, path, queries, headers, body)
  parameterDefinitions: ParameterInfo[] | null;
  swaggerOperationMetadata: SwaggerOperationMetadata | null;

  // Settings state
  settingsValues: NodeSettings | null;
  originalSettings: NodeSettings | null;
  settingsValidationErrors: Record<string, string>;

  // Connection state
  connectionPickerOpen: boolean;

  /**
   * Field-anchored fork popover state. Populated by `openFieldFork`,
   * cleared by `closeFieldFork` / `closeEditor`.
   */
  fieldFork: FieldForkState;

  /**
   * Selected tab in the WorkflowInputPanel rail. Resets to `'tokens'`
   * when the user navigates to a different node.
   */
  inputPanelValueSource: ValueSource;

  /**
   * Most recently focused parameter field (by `parameter.id`). Used by the
   * rail's "Insert template" handler to land the expression directly in the
   * field the user was editing, instead of dropping to a clipboard fallback.
   * Cleared on `closeEditor` and on a debounced blur from the field — see
   * `setLastFocusedParameterField` in `ParameterField`.
   */
  lastFocusedParameterField: string | null;

  // Actions
  openEditor: (node: SelectedNode) => void;
  closeEditor: () => Promise<void>;
  /**
   * Synchronous hard reset — wipes editor state without attempting any
   * autosave or any cross-store write. Used by `WorkflowDesignerPage` when
   * the route param flips workflows in place: `closeEditor()`'s autosave
   * would land in the NEW workflow's store (the URL has already
   * transitioned), corrupting it with the previous workflow's pending
   * edits. Also cancels the pending debounced autosave timer so a late
   * fire can't leak across workflows.
   */
  resetEditor: () => void;
  setInitialTab: (tab: ExpandedTab) => void;
  clearInitialTab: () => void;
  /** Toggle the editor between read-only and editable. */
  setIsReadOnly: (isReadOnly: boolean) => void;

  // Parameter actions
  setParameter: (path: string, value: ParameterValue) => void;
  setParameterSegments: (path: string, segments: ValueSegment[]) => void;
  setParameters: (values: Record<string, ParameterValue>) => void;
  getParameterAsString: (path: string) => string;
  getParameterAsSegments: (path: string) => ValueSegment[];
  resetParameters: () => void;
  setValidationError: (path: string, error: string | null) => void;
  clearValidationErrors: () => void;

  /**
   * Record a picker selection's value→display mapping. `previousValue` (the
   * value being replaced) is purged from the map so we don't leave stale
   * entries when the user clears the field, types a different literal, or
   * picks another item. Pass an empty/blank `display` to clear the entry
   * for `value`. Triggers auto-save like a parameter change.
   */
  setPickerDisplayValue: (
    value: string,
    display: string | undefined,
    previousValue?: string
  ) => void;

  // Swagger metadata actions (for ApiConnection serialization)
  /**
   * Set swagger operation metadata for proper ApiConnection serialization.
   * Called when parameters are loaded from swagger in ParameterList.
   * This enables proper serialization with method, path (with substitution), queries, etc.
   */
  setSwaggerMetadata: (
    definitions: ParameterInfo[],
    metadata: SwaggerOperationMetadata
  ) => void;
  /** Clear swagger metadata (called when editor closes) */
  clearSwaggerMetadata: () => void;

  // Multi-slot connection actions (for Agent operations)
  setSlotConnection: (slotId: string, referenceKey: string | null) => void;

  // Settings actions
  setSettings: (settings: NodeSettings) => void;
  initializeSettings: (settings: NodeSettings) => void;
  resetSettings: () => void;
  setSettingsValidationError: (key: string, error: string | null) => void;
  clearSettingsValidationErrors: () => void;
  hasSettingsValidationErrors: () => boolean;

  // Connection actions
  openConnectionPicker: () => void;
  closeConnectionPicker: () => void;
  setConnection: (connectionName: string) => void;



  /**
   * Append an expression-template literal segment to a parameter. Used by
   * the rail's "Insert template" button so the template lands in the field
   * the user was editing instead of falling back to clipboard.
   */
  insertExpressionToParameter: (parameterId: string, template: string) => void;

  /**
   * Update the parameter id whose editor most recently held focus. Pass
   * `null` to clear (callers typically debounce blur to avoid racing with
   * a click on the rail / chip).
   */
  setLastFocusedParameterField: (parameterId: string | null) => void;

  /**
   * Open the field-anchored fork popover for `parameterId`. Captures the
   * chip's bounding rect for positioning. The popover renders via portal
   * from ParameterField.
   *
   * `source`:
   *   - `'chip'`    — invoked from the FieldActionChips affordance (default)
   *   - `'rail'`    — invoked from a rail action
   *   - `'shortcut'`— invoked via keyboard shortcut
   * The `source` value is for telemetry only; it does not affect behavior.
   */
  openFieldFork: (
    parameterId: string,
    valueSource: ValueSource,
    anchor: DOMRect | FieldForkAnchor,
    source?: 'chip' | 'rail' | 'shortcut'
  ) => void;

  /** Switch the selected tab inside the active fork popover. */
  setFieldForkValueSource: (valueSource: ValueSource) => void;

  /**
   * Close the fork popover. `reason` is for telemetry only.
   */
  closeFieldFork: (reason?: 'esc' | 'outside_click' | 'commit') => void;

  /** Switch the selected tab in the WorkflowInputPanel rail. */
  setInputPanelValueSource: (valueSource: ValueSource) => void;

  // Node name update (for rename synchronization)
  setNodeName: (name: string) => void;
  setInnerActionName: (innerActionName: string) => void;

  // Save/sync actions
  applyChanges: () => Promise<{ success: boolean; errors?: Record<string, string> }>;
  /** Refresh parameter values from externally updated workflow (e.g., code view edits) */
  refreshFromWorkflow: (updatedNodeData: Action | Trigger) => void;
  discardChanges: () => void;
}

/**
 * Extract settings from a workflow node definition (deserialization)
 * Maps workflow JSON structure back to NodeSettings
 */
function extractSettingsFromNode(
  nodeData: Action | Trigger,
  isTrigger: boolean
): Partial<NodeSettings> {
  const settings: Partial<NodeSettings> = {};
  const inputs = (nodeData.inputs ?? {}) as Record<string, unknown>;
  const runtimeConfig = (nodeData as Record<string, unknown>).runtimeConfiguration as Record<string, unknown> | undefined;
  const operationOptions = (nodeData as Record<string, unknown>).operationOptions as string | undefined;

  // Timeout - from inputs.timeout
  if (inputs.timeout) {
    settings.timeout = { isSupported: true, value: inputs.timeout as string };
  }

  // Retry Policy - from inputs.retryPolicy
  // Heal stale workflows that were saved with an incomplete policy (e.g.
  // earlier versions wrote `{ type: 'fixed' }` without the required
  // count/interval). completeRetryPolicy() backfills missing required fields
  // from DEFAULT_RETRY_POLICY so the editor opens, displays, and re-serializes
  // a runtime-valid policy.
  if (inputs.retryPolicy) {
    const rp = inputs.retryPolicy as Record<string, unknown>;
    const raw: RetryPolicy = {
      type: ((rp.type as string) || 'default') as RetryPolicyType,
    };
    if (rp.count !== undefined) raw.count = rp.count as number;
    if (rp.interval) raw.interval = rp.interval as string;
    if (rp.minimumInterval) raw.minimumInterval = rp.minimumInterval as string;
    if (rp.maximumInterval) raw.maximumInterval = rp.maximumInterval as string;

    settings.retryPolicy = {
      isSupported: true,
      value: completeRetryPolicy(raw),
    };
  }

  // Concurrency - from runtimeConfiguration.concurrency
  // Runtime uses different JSON fields: "repetitions" for ForEach actions, "runs" for triggers.
  // Read both to handle workflows from any source.
  if (runtimeConfig?.concurrency) {
    const conc = runtimeConfig.concurrency as Record<string, unknown>;
    const repetitions = typeof conc.repetitions === 'number' ? conc.repetitions : undefined;
    const runs = typeof conc.runs === 'number' ? conc.runs : undefined;
    const resolvedRuns = repetitions ?? runs;

    // Only mark as enabled if an actual numeric value was found
    if (resolvedRuns !== undefined) {
      const concValue: {
        enabled: boolean;
        runs: number;
        maximumWaitingRuns?: number;
      } = {
        enabled: true,
        runs: resolvedRuns,
      };
      if (typeof conc.maximumWaitingRuns === 'number') {
        concValue.maximumWaitingRuns = conc.maximumWaitingRuns;
      }
      settings.concurrency = {
        isSupported: true,
        value: concValue,
      };
    }
  }

  // Secure Data - from runtimeConfiguration.secureData
  if (runtimeConfig?.secureData) {
    const sd = runtimeConfig.secureData as Record<string, unknown>;
    const props = sd.properties as string[] | undefined;
    settings.secureInputs = {
      isSupported: true,
      value: props?.includes('inputs') ?? false,
    };
    settings.secureOutputs = {
      isSupported: true,
      value: props?.includes('outputs') ?? false,
    };
  }

  // Paging - from runtimeConfiguration.paging
  if (runtimeConfig?.paging) {
    const paging = runtimeConfig.paging as Record<string, unknown>;
    const pagingValue: { enabled: boolean; value?: number } = { enabled: true };
    if (paging.value !== undefined) {
      pagingValue.value = paging.value as number;
    }
    settings.paging = {
      isSupported: true,
      value: pagingValue,
    };
  }

  // Request Options - from runtimeConfiguration.requestOptions
  if (runtimeConfig?.requestOptions) {
    const ro = runtimeConfig.requestOptions as Record<string, unknown>;
    const roValue: { timeout?: string } = {};
    if (ro.timeout) {
      roValue.timeout = ro.timeout as string;
    }
    settings.requestOptions = {
      isSupported: true,
      value: roValue,
    };
  }

  // Content Transfer (Chunking) - from runtimeConfiguration.contentTransfer
  if (runtimeConfig?.contentTransfer) {
    const ct = runtimeConfig.contentTransfer as Record<string, unknown>;
    const chunkValue: { transferMode?: 'chunked'; uploadChunkSize?: number } = {};
    if ((ct.transferMode as string)?.toLowerCase() === 'chunked') {
      chunkValue.transferMode = 'chunked';
    }
    if (ct.uploadChunkSize !== undefined) {
      chunkValue.uploadChunkSize = ct.uploadChunkSize as number;
    }
    settings.uploadChunk = {
      isSupported: true,
      value: chunkValue,
    };
  }

  // Tracked Properties - top-level
  if ((nodeData as Record<string, unknown>).trackedProperties) {
    settings.trackedProperties = {
      isSupported: true,
      value: (nodeData as Record<string, unknown>).trackedProperties as Record<string, string>,
    };
  }

  // Correlation - top-level (triggers only)
  if (isTrigger && (nodeData as Record<string, unknown>).correlation) {
    const corr = (nodeData as Record<string, unknown>).correlation as Record<string, unknown>;
    const corrValue: { clientTrackingId?: string } = {};
    if (corr.clientTrackingId) {
      corrValue.clientTrackingId = corr.clientTrackingId as string;
    }
    settings.correlation = {
      isSupported: true,
      value: corrValue,
    };
  }

  // SplitOn - top-level (triggers only)
  if (isTrigger && (nodeData as Record<string, unknown>).splitOn) {
    settings.splitOn = {
      isSupported: true,
      value: {
        enabled: true,
        value: (nodeData as Record<string, unknown>).splitOn as string,
      },
    };
  }

  // Condition Expressions - from conditions array (triggers only)
  if (isTrigger && (nodeData as Record<string, unknown>).conditions) {
    const conditions = (nodeData as Record<string, unknown>).conditions as Array<{ expression?: string }>;
    const expressions = conditions
      .map((c) => c.expression)
      .filter((e): e is string => typeof e === 'string');
    if (expressions.length > 0) {
      settings.conditionExpressions = {
        isSupported: true,
        value: expressions,
      };
    }
  }

  // Operation Options - parse comma-separated string
  if (operationOptions) {
    const opts = operationOptions.split(',').map((o) => o.trim().toLowerCase());
    if (opts.includes('disableasyncpattern')) {
      settings.disableAsyncPattern = { isSupported: true, value: true };
    }
    if (opts.includes('suppressworkflowheaders')) {
      settings.suppressWorkflowHeaders = { isSupported: true, value: true };
    }
    if (opts.includes('suppressworkflowheadersonresponse')) {
      settings.suppressWorkflowHeadersOnResponse = { isSupported: true, value: true };
    }
    if (opts.includes('disableautomaticdecompression')) {
      settings.disableAutomaticDecompression = { isSupported: true, value: true };
    }
    if (opts.includes('enableschemavalidation')) {
      settings.requestSchemaValidation = { isSupported: true, value: true };
    }
  }

  // RunAfter - top-level (actions only, not triggers)
  if (!isTrigger && (nodeData as Record<string, unknown>).runAfter) {
    const runAfter = (nodeData as Record<string, unknown>).runAfter as RunAfterSettings;
    // Only set if there are actual dependencies
    if (Object.keys(runAfter).length > 0) {
      settings.runAfter = {
        isSupported: true,
        value: runAfter,
      };
    }
  }

  // Limit - top-level (Agent and loop actions)
  // Contains timeout and count for execution limits
  const limit = (nodeData as Record<string, unknown>).limit as { timeout?: string; count?: number | string } | undefined;
  if (limit) {
    // Extract timeout from limit (overrides inputs.timeout if both present)
    if (limit.timeout) {
      settings.timeout = { isSupported: true, value: limit.timeout };
    }
    // Extract count from limit
    if (limit.count !== undefined) {
      settings.count = { isSupported: true, value: limit.count };
    }
  }

  return settings;
}

/**
 * Serialized settings output structure
 * Settings are distributed across multiple locations in the workflow definition
 */
interface SerializedSettings {
  /** Settings that go into inputs (timeout, retryPolicy) */
  inputs: Record<string, unknown>;
  /**
   * Inputs keys that should be REMOVED from the existing node inputs (e.g. when
   * the user reverts a previously-saved retryPolicy back to the runtime default,
   * we need to drop the stale value so it isn't carried through unchanged).
   */
  removeFromInputs: string[];
  /** Settings that go into runtimeConfiguration */
  runtimeConfiguration: Record<string, unknown> | null;
  /** Top-level properties (trackedProperties, correlation, splitOn, conditions) */
  topLevel: Record<string, unknown>;
  /** Operation options string (comma-separated flags) */
  operationOptions: string | null;
}

/**
 * Serialize NodeSettings to workflow definition format
 * Maps settings to their correct locations in the workflow JSON
 */
function serializeSettingsToWorkflow(
  settings: NodeSettings | null,
  isTrigger: boolean,
  operationType?: string
): SerializedSettings {
  const result: SerializedSettings = {
    inputs: {},
    removeFromInputs: [],
    runtimeConfiguration: null,
    topLevel: {},
    operationOptions: null,
  };

  if (!settings) return result;

  const runtimeConfig: Record<string, unknown> = {};
  const operationOptionsList: string[] = [];

  // === INPUTS SETTINGS ===

  // Timeout - goes to inputs.timeout for most actions,
  // but for Until/Agent actions (which have count setting), timeout goes into limit instead
  const timeoutBelongsInLimit = settings.count?.isSupported;
  if (settings.timeout?.isSupported && settings.timeout.value && !timeoutBelongsInLimit) {
    result.inputs.timeout = settings.timeout.value;
  }

  // Retry Policy - goes to inputs.retryPolicy
  // serializeRetryPolicyForInputs() returns null for `type: 'default'` (a
  // UI-only sentinel — the runtime rejects 'default' as a RetryType). When
  // null, we strip any previously-saved value so the runtime applies its
  // built-in default. Otherwise it returns a clean per-type payload.
  if (settings.retryPolicy?.isSupported && settings.retryPolicy.value) {
    const serialized = serializeRetryPolicyForInputs(settings.retryPolicy.value);
    if (serialized === null) {
      result.removeFromInputs.push('retryPolicy');
    } else {
      result.inputs.retryPolicy = serialized;
    }
  }

  // === RUNTIME CONFIGURATION SETTINGS ===

  // Concurrency
  // Runtime uses different JSON fields: "repetitions" for ForEach actions, "runs" for triggers.
  // ForEach also forbids "maximumWaitingRuns".
  if (settings.concurrency?.isSupported && settings.concurrency.value?.enabled) {
    const opLower = operationType?.toLowerCase();
    const isForeach = opLower === 'foreach' || opLower === 'for_each';
    if (isForeach) {
      // ForEach: runtime requires "repetitions", rejects "runs" and "maximumWaitingRuns"
      runtimeConfig.concurrency = {
        repetitions: settings.concurrency.value.runs,
      };
    } else if (isTrigger) {
      // Triggers: runtime requires "runs", rejects "repetitions"
      runtimeConfig.concurrency = {
        runs: settings.concurrency.value.runs,
        ...(settings.concurrency.value.maximumWaitingRuns !== undefined && {
          maximumWaitingRuns: settings.concurrency.value.maximumWaitingRuns,
        }),
      };
    }
    // Other action types: concurrency not emitted (unsupported by runtime)
  }

  // Secure Data
  const secureProps: string[] = [];
  if (settings.secureInputs?.isSupported && settings.secureInputs.value) {
    secureProps.push('inputs');
  }
  if (settings.secureOutputs?.isSupported && settings.secureOutputs.value) {
    secureProps.push('outputs');
  }
  if (secureProps.length > 0) {
    runtimeConfig.secureData = { properties: secureProps };
  }

  // Paging
  if (settings.paging?.isSupported && settings.paging.value?.enabled) {
    runtimeConfig.paging = {
      enabled: true,
      ...(settings.paging.value.value !== undefined && { value: settings.paging.value.value }),
    };
  }

  // Request Options
  if (settings.requestOptions?.isSupported && settings.requestOptions.value?.timeout) {
    runtimeConfig.requestOptions = {
      timeout: settings.requestOptions.value.timeout,
    };
  }

  // Content Transfer (Chunking)
  if (settings.uploadChunk?.isSupported && settings.uploadChunk.value?.transferMode === 'chunked') {
    runtimeConfig.contentTransfer = {
      transferMode: 'Chunked',
      ...(settings.uploadChunk.value.uploadChunkSize !== undefined && {
        uploadChunkSize: settings.uploadChunk.value.uploadChunkSize,
      }),
    };
  }

  if (Object.keys(runtimeConfig).length > 0) {
    result.runtimeConfiguration = runtimeConfig;
  }

  // === TOP-LEVEL SETTINGS ===

  // Tracked Properties
  if (settings.trackedProperties?.isSupported && settings.trackedProperties.value) {
    const props = settings.trackedProperties.value;
    if (Object.keys(props).length > 0) {
      result.topLevel.trackedProperties = props;
    }
  }

  // Correlation (triggers only)
  if (isTrigger && settings.correlation?.isSupported && settings.correlation.value?.clientTrackingId) {
    result.topLevel.correlation = {
      clientTrackingId: settings.correlation.value.clientTrackingId,
    };
  }

  // SplitOn (triggers only)
  if (isTrigger && settings.splitOn?.isSupported && settings.splitOn.value?.enabled && settings.splitOn.value.value) {
    result.topLevel.splitOn = settings.splitOn.value.value;
  }

  // Condition Expressions (triggers only)
  if (isTrigger && settings.conditionExpressions?.isSupported && settings.conditionExpressions.value) {
    const expressions = settings.conditionExpressions.value.filter((e) => e.trim());
    if (expressions.length > 0) {
      result.topLevel.conditions = expressions.map((expression) => ({ expression }));
    }
  }

  // RunAfter (actions only, not triggers)
  if (!isTrigger && settings.runAfter?.isSupported && settings.runAfter.value) {
    const runAfterValue = settings.runAfter.value;
    // Only include if there are actual dependencies
    if (Object.keys(runAfterValue).length > 0) {
      result.topLevel.runAfter = runAfterValue;
    }
  }

  // Limit - top-level (Agent and loop actions)
  // Contains timeout and count for execution limits
  // Note: For Agent actions, timeout/count should be in limit rather than inputs
  const limitTimeout = settings.timeout?.isSupported ? settings.timeout.value : undefined;
  const limitCount = settings.count?.isSupported ? settings.count.value : undefined;
  if (limitTimeout || limitCount !== undefined) {
    const limit: { timeout?: string; count?: number | string } = {};
    if (limitTimeout) {
      limit.timeout = limitTimeout;
    }
    if (limitCount !== undefined) {
      limit.count = limitCount;
    }
    result.topLevel.limit = limit;
  }

  // === OPERATION OPTIONS (comma-separated string) ===

  if (settings.disableAsyncPattern?.isSupported && settings.disableAsyncPattern.value) {
    operationOptionsList.push('DisableAsyncPattern');
  }
  if (settings.suppressWorkflowHeaders?.isSupported && settings.suppressWorkflowHeaders.value) {
    operationOptionsList.push('SuppressWorkflowHeaders');
  }
  if (settings.suppressWorkflowHeadersOnResponse?.isSupported && settings.suppressWorkflowHeadersOnResponse.value) {
    operationOptionsList.push('SuppressWorkflowHeadersOnResponse');
  }
  if (settings.disableAutomaticDecompression?.isSupported && settings.disableAutomaticDecompression.value) {
    operationOptionsList.push('DisableAutomaticDecompression');
  }
  if (settings.requestSchemaValidation?.isSupported && settings.requestSchemaValidation.value) {
    operationOptionsList.push('EnableSchemaValidation');
  }

  if (operationOptionsList.length > 0) {
    result.operationOptions = operationOptionsList.join(',');
  }

  return result;
}

/**
 * Options for building serialized node data
 */
export interface BuildSerializedNodeDataOptions {
  /** The original node data */
  nodeData: Record<string, unknown>;
  /** The node type (trigger, action, tool) */
  nodeType: 'trigger' | 'action' | 'tool';
  /** The operation type (e.g., 'ApiConnection', 'Compose') */
  operationType: string | undefined;
  /** Current parameter values from the editor */
  parameterValues: Record<string, ParameterValue>;
  /** Current settings from the editor */
  settingsValues: NodeSettings | null;
  /** Swagger parameter definitions (for ApiConnection serialization) */
  parameterDefinitions?: ParameterInfo[] | undefined;
  /** Swagger operation metadata (method, path template, etc.) */
  swaggerOperationMetadata?: SwaggerOperationMetadata | undefined;
  /** File-picker `{ value: display }` map written to `metadata`. */
  pickerDisplayValues?: Record<string, string> | undefined;
  /** Workflow kind (for Agent limit handling) */
  workflowKind?: string | undefined;
}

/**
 * Build serialized node data from current parameter and settings values.
 * This is the canonical function for serializing node data - used by both
 * the save logic (applyChanges) and the live preview (Code tab).
 *
 * @returns The serialized node data ready for saving to the workflow
 */
export function buildSerializedNodeData(options: BuildSerializedNodeDataOptions): Record<string, unknown> {
  const {
    nodeData,
    nodeType,
    operationType,
    parameterValues,
    settingsValues,
    parameterDefinitions,
    swaggerOperationMetadata,
    pickerDisplayValues,
    workflowKind,
  } = options;

  const isTrigger = nodeType === 'trigger';

  // Check if this is an action type that uses primitive inputs (not an object)
  const isPrimitiveInputsAction = operationType === 'Compose';
  const originalInputs = nodeData.inputs;

  // Serialize settings to workflow format
  const serializedSettings = serializeSettingsToWorkflow(settingsValues, isTrigger, operationType);

  let finalInputs: Record<string, unknown> | string | number | boolean | unknown[];

  if (isPrimitiveInputsAction) {
    // For Compose and similar actions, inputs is the direct value
    const inputsParam = parameterValues['inputs'];
    if (isValueSegmentArray(inputsParam)) {
      const serialized = serializeSegmentsToValue(inputsParam);
      // Parse JSON strings back to objects/arrays so {"key":"value"} doesn't
      // get double-stringified in the workflow definition.
      finalInputs = serializeParameterValue(serialized) as typeof finalInputs;
    } else if (inputsParam != null) {
      finalInputs = serializeParameterValue(inputsParam) as typeof finalInputs;
    } else {
      finalInputs = originalInputs as typeof finalInputs;
    }
  } else {
    // Standard object-based inputs - build from parameters
    const inputsAsObject = (originalInputs as Record<string, unknown>) || {};
    finalInputs = buildInputsFromParameters(
      inputsAsObject,
      parameterValues,
      operationType,
      parameterDefinitions,
      swaggerOperationMetadata
    );
  }

  // Build updated node data
  const result: Record<string, unknown> = {
    ...nodeData,
    inputs: finalInputs,
  };

  // Merge settings-level inputs (retryPolicy, timeout) into node inputs BEFORE
  // inputsLocation processing, since non-default inputsLocation deletes result.inputs.
  // Also strip any settings keys flagged for removal (e.g. retryPolicy reverted to
  // the runtime default) so stale values don't survive the round-trip.
  const hasInputAdditions = Object.keys(serializedSettings.inputs).length > 0;
  const hasInputRemovals = serializedSettings.removeFromInputs.length > 0;
  if (
    (hasInputAdditions || hasInputRemovals) &&
    typeof result.inputs === 'object' &&
    !Array.isArray(result.inputs)
  ) {
    const merged: Record<string, unknown> = {
      ...(result.inputs as Record<string, unknown>),
      ...serializedSettings.inputs,
    };
    for (const key of serializedSettings.removeFromInputs) {
      delete merged[key];
    }
    result.inputs = merged;
  }

  // Generic inputsLocation handling for serialization — use manifest to determine
  // where inputs should be written instead of hardcoding per operation type
  const manifest = operationType ? getBuiltInManifest(operationType) : null;
  const inputsLocation = manifest?.properties?.inputsLocation;
  const hasNonDefaultInputsLocation = !!inputsLocation && inputsLocation[0] !== 'inputs';

  if (hasNonDefaultInputsLocation) {
    // Use result.inputs (not finalInputs) so settings merged at line 690-694
    // (retryPolicy, timeout) are included in the relocation
    const mergedInputs = result.inputs;
    if (typeof mergedInputs === 'object' && !Array.isArray(mergedInputs)) {
      const inputsObj = mergedInputs as Record<string, unknown>;

      if (inputsLocation.length === 1 && inputsLocation[0]) {
        // Single non-default path like ['expression'] — extract value to that key
        const key = inputsLocation[0];
        if (inputsObj[key] !== undefined) {
          result[key] = inputsObj[key];
        }
      } else if (inputsLocation.length === 0) {
        // Empty path — spread all input properties to top level
        for (const [key, value] of Object.entries(inputsObj)) {
          if (value !== undefined) {
            result[key] = value;
          }
        }
      }

      delete result.inputs;
    }
  }

  // Add runtimeConfiguration if present
  if (serializedSettings.runtimeConfiguration) {
    result.runtimeConfiguration = serializedSettings.runtimeConfiguration;
  } else {
    delete result.runtimeConfiguration;
  }

  // Add top-level settings (trackedProperties, correlation, splitOn, conditions)
  Object.entries(serializedSettings.topLevel).forEach(([key, value]) => {
    result[key] = value;
  });

  // Restore recurrence for triggers
  if (isTrigger && parameterValues['_recurrence']) {
    result.recurrence = parameterValues['_recurrence'];
  }

  // Add default recurrence for polling triggers that don't have one
  if (isTrigger && operationType === 'ApiConnection' && !result.recurrence) {
    result.recurrence = {
      frequency: 'Minute',
      interval: 1,
    };
  }

  // Clean up top-level settings that were removed
  const topLevelKeys = ['trackedProperties', 'correlation', 'splitOn', 'conditions', 'limit', 'recurrence'];
  topLevelKeys.forEach((key) => {
    if (!(key in serializedSettings.topLevel) && key in result) {
      // Don't delete recurrence if it was restored or is a default
      if (key === 'recurrence' && (parameterValues['_recurrence'] || operationType === 'ApiConnection')) {
        return;
      }
      delete result[key];
    }
  });

  // Handle runAfter
  if ('runAfter' in serializedSettings.topLevel) {
    result.runAfter = serializedSettings.topLevel.runAfter;
  }

  // Ensure runAfter exists for all actions (not triggers)
  if (nodeType === 'action' || nodeType === 'tool') {
    if (!('runAfter' in result)) {
      result.runAfter = {};
    }
  }

  // Ensure limit exists for Until actions
  if (operationType === 'Until' && !('limit' in result)) {
    result.limit = {
      count: 60,
      timeout: 'PT1H',
    };
  }

  // Add operationOptions if present
  if (serializedSettings.operationOptions) {
    result.operationOptions = serializedSettings.operationOptions;
  } else {
    delete result.operationOptions;
  }

  // For Agent actions in stateful workflows, ensure limit property has defaults
  const isStatefulWorkflow = workflowKind === 'Stateful' || !workflowKind;
  if (operationType === 'Agent' && isStatefulWorkflow) {
    const existingLimit = result.limit as { count?: number | string; timeout?: string } | undefined;
    if (!existingLimit) {
      result.limit = {
        count: 60,
        timeout: 'PT1H',
      };
    } else {
      if (existingLimit.count === undefined) {
        existingLimit.count = 60;
      }
      if (!existingLimit.timeout) {
        existingLimit.timeout = 'PT1H';
      }
    }
  }

  // Merge picker display values into `metadata`; reserved keys on the
  // existing action (position, swaggerPathTemplate, etc.) are preserved.
  // Omit `metadata` entirely when nothing remains so we don't write
  // `metadata: {}` into the workflow.
  const existingMetadata = result.metadata as Record<string, unknown> | undefined;
  const nextMetadata = serializePickerDisplayValuesToMetadata(
    existingMetadata,
    pickerDisplayValues ?? {}
  );
  if (nextMetadata) {
    result.metadata = nextMetadata;
  } else {
    delete result.metadata;
  }

  return result;
}

// Export for use in hooks
export { extractSettingsFromNode };

export const useNodeEditorStore = create<NodeEditorState>()(
  immer((set, get) => ({
    // Initial state
    isOpen: false,
    initialTab: null,
    isReadOnly: false,
    node: null,
    parameterValues: {},
    originalValues: {},
    isDirty: false,
    validationErrors: {},
    pickerDisplayValues: {},
    parameterDefinitions: null,
    swaggerOperationMetadata: null,
    settingsValues: null,
    originalSettings: null,
    settingsValidationErrors: {},
    connectionPickerOpen: false,
    fieldFork: { ...INITIAL_FIELD_FORK_STATE },
    inputPanelValueSource: 'tokens',
    lastFocusedParameterField: null,

    // Modal actions
    openEditor: (node) => {
      // MCP tools have no inner action; skip parameter extraction entirely.
      // The MCP config tab reads inputs.parameters.allowedTools directly via
      // setToolMcpAllowedTools and does not use the parameterValues bag.
      const isMcp = node.type === 'tool' && node.isMcpTool === true;
      const values = isMcp ? {} : extractParameterValues(node.nodeData);

      // For tool nodes, also extract tool metadata (name, description, schema)
      if (node.type === 'tool' && node.toolData) {
        values['_toolName'] = node.toolName || '';
        values['_toolDescription'] = node.toolData.description || '';
        values['_agentParameterSchema'] = node.toolData.agentParameterSchema || null;
        // Store the inner action name for potential renaming
        values['_innerActionName'] = node.innerActionName || '';
      }
      
      // Check if node requires a connection but doesn't have one yet
      // If so, open directly to the connection tab. Skip for MCP tools.
      const nodeType = (node.nodeData as Record<string, unknown>)?.type as string | undefined;
      let shouldOpenConnectionTab = false;
      if (!isMcp && nodeType) {
        const manifest = getBuiltInManifest(nodeType);
        const needsConnection = manifest?.properties.connection !== undefined
          ? requiresConnection(manifest)
          : operationRequiresConnection(nodeType);
        if (needsConnection) {
          // Check if the node's definition already has a connection reference embedded.
          // This is more reliable than the connection store mapping, which may lag behind.
          const inputs = (node.nodeData as Record<string, unknown>)?.inputs as Record<string, unknown> | undefined;
          const hasConnectionRef = !!(
            values['_connection'] ||
            values['_modelConnection_model1'] ||
            (inputs?.serviceProviderConfiguration as Record<string, unknown>)?.connectionName
          );
          if (!hasConnectionRef) {
            shouldOpenConnectionTab = true;
          }
        }
      }

      set((state) => {
        state.isOpen = true;
        state.node = node;
        state.parameterValues = values;
        state.originalValues = { ...values };
        state.isDirty = false;
        state.validationErrors = {};
        state.pickerDisplayValues = extractPickerDisplayValues(node.nodeData);
        state.settingsValues = null;
        state.originalSettings = null;
        state.settingsValidationErrors = {};
        // Clear swagger metadata - will be populated by ParameterList when parameters load
        state.parameterDefinitions = null;
        state.swaggerOperationMetadata = null;
        // Navigate to connection tab if connection is required but missing
        if (shouldOpenConnectionTab) {
          state.initialTab = 'connection';
        }
      });
    },

    closeEditor: async () => {
      // Trigger immediate save if dirty (don't wait for debounce)
      const { isDirty } = get();
      if (isDirty) {
        // Clear any pending debounce timer
        if (autoSaveTimerId) {
          clearTimeout(autoSaveTimerId);
          autoSaveTimerId = null;
        }
        // Apply changes synchronously in background - don't block close
        get().applyChanges().catch((err) => {
          console.error('[nodeEditorStore] Final save on close failed:', err);
        });
      }

      // First clear the workflow selection to prevent NodeEditor sync effect
      // from reopening the editor (it checks for workflowSelectedNode && !isOpen)
      // Use dynamic import to avoid circular dependency
      // Note: Use closeNodeEditor (not clearSelection) to preserve multi-selection
      const { useWorkflowStore } = await import('@/stores/workflowStore');
      useWorkflowStore.getState().closeNodeEditor();

      // Then close the editor immediately
      set((state) => {
        state.isOpen = false;
        state.node = null;
        state.parameterValues = {};
        state.originalValues = {};
        state.isDirty = false;
        state.validationErrors = {};
        state.pickerDisplayValues = {};
        state.settingsValues = null;
        state.originalSettings = null;
        state.settingsValidationErrors = {};
        state.connectionPickerOpen = false;
        state.fieldFork = { ...INITIAL_FIELD_FORK_STATE };
        state.inputPanelValueSource = 'tokens';
        state.lastFocusedParameterField = null;
        // Clear swagger metadata
        state.parameterDefinitions = null;
        state.swaggerOperationMetadata = null;
      });
    },

    // Synchronous hard reset — see interface doc on `resetEditor`. Mirrors
    // the field-by-field wipe inside `closeEditor`, plus cancels the
    // debounced autosave timer and clears `initialTab` so a stale tab
    // selection doesn't pre-target the next opened editor. Does NOT call
    // `applyChanges()` and does NOT touch `workflowStore` — the caller
    // (currently `WorkflowDesignerPage`'s workflow-switch effect) is
    // expected to clear `workflowStore.selectedNode` first to avoid the
    // NodeEditor sync effect re-opening from a stale selection.
    resetEditor: () => {
      if (autoSaveTimerId) {
        clearTimeout(autoSaveTimerId);
        autoSaveTimerId = null;
      }
      set((state) => {
        state.isOpen = false;
        state.initialTab = null;
        state.node = null;
        state.parameterValues = {};
        state.originalValues = {};
        state.isDirty = false;
        state.validationErrors = {};
        state.pickerDisplayValues = {};
        state.settingsValues = null;
        state.originalSettings = null;
        state.settingsValidationErrors = {};
        state.connectionPickerOpen = false;
        state.fieldFork = { ...INITIAL_FIELD_FORK_STATE };
        state.inputPanelValueSource = 'tokens';
        state.lastFocusedParameterField = null;
        state.parameterDefinitions = null;
        state.swaggerOperationMetadata = null;
      });
    },

    setInitialTab: (tab) => {
      set((state) => {
        state.initialTab = tab;
      });
    },

    clearInitialTab: () => {
      set((state) => {
        state.initialTab = null;
      });
    },

    setIsReadOnly: (isReadOnly) => {
      set((state) => {
        state.isReadOnly = isReadOnly;
      });
    },

    // Parameter actions - all trigger auto-save
    setParameter: (path, value) => {
      if (get().isReadOnly) return;
      set((state) => {
        state.parameterValues[path] = value;
        state.isDirty = true;
        // Clear validation error when user edits
        if (state.validationErrors[path]) {
          delete state.validationErrors[path];
        }
      });
      // Schedule auto-save after state update
      scheduleAutoSave(() => get().applyChanges());
    },

    setParameterSegments: (path, segments) => {
      if (get().isReadOnly) return;
      set((state) => {
        state.parameterValues[path] = segments;
        state.isDirty = true;
        // Clear validation error when user edits
        if (state.validationErrors[path]) {
          delete state.validationErrors[path];
        }
      });
      // Schedule auto-save after state update
      scheduleAutoSave(() => get().applyChanges());
    },

    setParameters: (values) => {
      if (get().isReadOnly) return;
      set((state) => {
        state.parameterValues = { ...state.parameterValues, ...values };
        state.isDirty = true;
      });
      // Schedule auto-save after state update
      scheduleAutoSave(() => get().applyChanges());
    },

    setPickerDisplayValue: (value, display, previousValue) => {
      if (get().isReadOnly) return;

      const trimmed = display?.trim();
      // Drop the previous entry whenever it differs from the new value —
      // covers clears (value === '') and replacements (different id).
      const stale = previousValue && previousValue !== value ? previousValue : undefined;

      // Nothing to write or clean up.
      if (!stale && (!value || !trimmed)) return;

      set((state) => {
        if (stale) {
          delete state.pickerDisplayValues[stale];
        }
        if (value) {
          if (trimmed) {
            state.pickerDisplayValues[value] = trimmed;
          } else {
            delete state.pickerDisplayValues[value];
          }
        }
        state.isDirty = true;
      });
      scheduleAutoSave(() => get().applyChanges());
    },

    getParameterAsString: (path) => {
      const { parameterValues } = get();
      const value = parameterValues[path];
      if (value === null || value === undefined) return '';
      if (isValueSegmentArray(value)) {
        return serializeSegmentsToValue(value);
      }
      return String(value);
    },

    getParameterAsSegments: (path) => {
      const { parameterValues } = get();
      const value = parameterValues[path];
      if (isValueSegmentArray(value)) {
        return value;
      }
      // Parse string value to segments
      if (typeof value === 'string') {
        const result = parseValueToSegments(value);
        return result.segments;
      }
      // For other types, create a single literal segment
      if (value !== null && value !== undefined) {
        return [{
          id: `seg_${Date.now()}`,
          type: 'literal' as const,
          value: String(value),
        }];
      }
      return [];
    },

    resetParameters: () => {
      set((state) => {
        state.parameterValues = { ...state.originalValues };
        state.isDirty = false;
        state.validationErrors = {};
      });
    },

    setValidationError: (path, error) => {
      set((state) => {
        if (error) {
          state.validationErrors[path] = error;
        } else {
          delete state.validationErrors[path];
        }
      });
    },

    clearValidationErrors: () => {
      set((state) => {
        state.validationErrors = {};
      });
    },

    // Swagger metadata actions (for ApiConnection serialization)
    setSwaggerMetadata: (definitions, metadata) => {
      set((state) => {
        state.parameterDefinitions = definitions;
        state.swaggerOperationMetadata = metadata;
      });
    },

    clearSwaggerMetadata: () => {
      set((state) => {
        state.parameterDefinitions = null;
        state.swaggerOperationMetadata = null;
      });
    },

    // Multi-slot connection actions (for Agent operations)
    setSlotConnection: (slotId, referenceKey) => {
      if (get().isReadOnly) return;
      set((state) => {
        const key = `_modelConnection_${slotId}`;
        const previous = state.parameterValues[key] as string | undefined;
        if (referenceKey) {
          state.parameterValues[key] = referenceKey;
        } else {
          delete state.parameterValues[key];
        }
        // For the agent model slot, available deployments/models depend on the
        // connection's account/project, so reset model-bound parameters when
        // the connection changes. `deploymentId` (AzureOpenAI / Foundry / APIM)
        // and `modelId` (V1 Chat Completions) are mutually exclusive via
        // x-ms-input-dependencies, so clearing both is safe.
        // Only clear when the connection actually changed — re-selecting the
        // same connection should not discard the user's model choice.
        if (slotId === 'model1' && referenceKey !== previous) {
          delete state.parameterValues['deploymentId'];
          delete state.parameterValues['modelId'];
        }
        state.isDirty = true;
      });
      // Schedule auto-save after connection change
      scheduleAutoSave(() => get().applyChanges());
    },

    // Settings actions - trigger auto-save
    setSettings: (settings) => {
      if (get().isReadOnly) return;
      set((state) => {
        state.settingsValues = settings;
        state.isDirty = true;
      });
      // Schedule auto-save after settings change
      scheduleAutoSave(() => get().applyChanges());
    },

    initializeSettings: (settings) => {
      set((state) => {
        // Deep clone to prevent shared reference issues
        state.settingsValues = deepClone(settings);
        state.originalSettings = deepClone(settings);
      });
    },

    resetSettings: () => {
      // Clone outside set() to avoid Immer proxy issues with structuredClone
      const { originalSettings } = get();
      const clonedSettings = originalSettings ? structuredClone(originalSettings) : null;

      set((state) => {
        state.settingsValues = clonedSettings;
      });
    },

    setSettingsValidationError: (key, error) => {
      set((state) => {
        if (error) {
          state.settingsValidationErrors[key] = error;
        } else {
          delete state.settingsValidationErrors[key];
        }
      });
    },

    clearSettingsValidationErrors: () => {
      set((state) => {
        state.settingsValidationErrors = {};
      });
    },

    hasSettingsValidationErrors: () => {
      const { settingsValidationErrors } = get();
      return Object.keys(settingsValidationErrors).length > 0;
    },

    // Connection actions
    openConnectionPicker: () => {
      set((state) => {
        state.connectionPickerOpen = true;
      });
    },

    closeConnectionPicker: () => {
      set((state) => {
        state.connectionPickerOpen = false;
      });
    },

    setConnection: (connectionName) => {
      if (get().isReadOnly) return;
      set((state) => {
        state.parameterValues['_connection'] = connectionName;
        state.isDirty = true;
        state.connectionPickerOpen = false;
      });
      // Schedule auto-save after connection change (writes referenceName to workflow definition)
      scheduleAutoSave(() => get().applyChanges());
    },

    insertExpressionToParameter: (parameterId, template) => {
      if (!template) return;
      if (get().isReadOnly) return;
      set((state) => {
        const currentValue = state.parameterValues[parameterId];
        // Wrap the function template in ${...} so the runtime evaluates
        // it as an expression. This mirrors FieldForkContainer.handle-
        // ExpressionInsert and the drag-and-drop handler in
        // TokenizedInput — bare templates would be treated as literal
        // strings at evaluation time.
        const literalSegment: ValueSegment = {
          id: generateSegmentId(),
          type: ValueSegmentType.LITERAL,
          value: wrapAsExpression(template),
        };

        if (isValueSegmentArray(currentValue)) {
          // Append the template as its own literal segment so token segments
          // earlier in the value stay intact. Mirrors the field-fork insert
          // path on FieldForkContainer.handleExpressionInsert.
          state.parameterValues[parameterId] = [...currentValue, literalSegment];
        } else if (typeof currentValue === 'string' && currentValue.length > 0) {
          state.parameterValues[parameterId] = [
            { id: generateSegmentId(), type: ValueSegmentType.LITERAL, value: currentValue },
            literalSegment,
          ];
        } else {
          state.parameterValues[parameterId] = [literalSegment];
        }

        state.isDirty = true;
      });
    },

    setLastFocusedParameterField: (parameterId) => {
      // Avoid pointless writes — keeps subscribed selectors quiet on idle
      // focus events.
      if (get().lastFocusedParameterField === parameterId) return;
      set((state) => {
        state.lastFocusedParameterField = parameterId;
      });
    },

    openFieldFork: (parameterId, valueSource, anchor, source = 'chip') => {
      // Normalize DOMRect (which is a live, non-serializable browser object) to
      // a plain snapshot so the store stays serializable and immer-friendly.
      const normalizedAnchor: FieldForkAnchor = {
        top: anchor.top,
        left: anchor.left,
        right: anchor.right,
        bottom: anchor.bottom,
        width: anchor.width,
        height: anchor.height,
      };
      fieldForkOpenedAt = Date.now();
      set((state) => {
        state.fieldFork = {
          open: true,
          parameterId,
          valueSource,
          anchor: normalizedAnchor,
        };
      });
      telemetryService.trackEvent('tokenpicker.fork.opened', {
        source,
        tab: valueSource,
      });
    },

    setFieldForkValueSource: (valueSource) => {
      const prev = get().fieldFork.valueSource;
      if (prev === valueSource) return;
      set((state) => {
        state.fieldFork.valueSource = valueSource;
      });
      const msSinceOpen =
        fieldForkOpenedAt !== null ? Date.now() - fieldForkOpenedAt : 0;
      telemetryService.trackEvent(
        'tokenpicker.fork.tab_switched',
        { from: prev, to: valueSource },
        { ms_since_open: msSinceOpen }
      );
    },

    closeFieldFork: (reason = 'commit') => {
      const wasOpen = get().fieldFork.open;
      if (!wasOpen) return;
      set((state) => {
        state.fieldFork = { ...INITIAL_FIELD_FORK_STATE };
      });
      fieldForkOpenedAt = null;
      telemetryService.trackEvent('tokenpicker.fork.dismissed', { reason });
    },

    setInputPanelValueSource: (valueSource) => {
      const prev = get().inputPanelValueSource;
      if (prev === valueSource) return;
      set((state) => {
        state.inputPanelValueSource = valueSource;
      });
      telemetryService.trackEvent('tokenpicker.rail.tab_switched', {
        from: prev,
        to: valueSource,
      });
    },

    // Save/sync actions
    refreshFromWorkflow: (updatedNodeData) => {
      const { node, isOpen } = get();
      if (!isOpen || !node) return;

      // Re-extract parameter values from the updated node data
      const values = extractParameterValues(updatedNodeData);

      // For tool nodes, preserve tool metadata
      if (node.type === 'tool' && node.toolData) {
        values['_toolName'] = node.toolName || '';
        values['_toolDescription'] = node.toolData.description || '';
        values['_agentParameterSchema'] = node.toolData.agentParameterSchema || null;
        values['_innerActionName'] = node.innerActionName || '';
      }

      // Update the node editor state without marking dirty
      // (the change came from the code view, not from user editing the panel)
      set((state) => {
        state.node = { ...node, nodeData: updatedNodeData };
        state.parameterValues = values;
        state.originalValues = { ...values };
        state.pickerDisplayValues = extractPickerDisplayValues(updatedNodeData);
        state.isDirty = false;
      });
    },

    applyChanges: async () => {
      const { node, parameterValues, settingsValues, parameterDefinitions, swaggerOperationMetadata, pickerDisplayValues, isReadOnly } = get();
      if (!node) return { success: false, errors: { _general: 'No node selected' } };
      // Read-only mode: drop the auto-save no-op so we never persist
      // changes that the UI shouldn't have produced in the first place.
      if (isReadOnly) return { success: true };

      const nodeType = node.nodeData?.type;

      // Import workflowStore dynamically to avoid circular dependency
      const { useWorkflowStore } = await import('@/stores/workflowStore');
      const workflowStore = useWorkflowStore.getState();

      // Build serialized node data using the reusable function
      const updatedNodeData = buildSerializedNodeData({
        nodeData: node.nodeData as Record<string, unknown>,
        nodeType: node.type,
        operationType: nodeType,
        parameterValues,
        settingsValues,
        parameterDefinitions: parameterDefinitions ?? undefined,
        swaggerOperationMetadata: swaggerOperationMetadata ?? undefined,
        pickerDisplayValues,
        workflowKind: workflowStore.workflow?.kind,
      });

      if (node.type === 'action') {
        workflowStore.updateAction(node.name, updatedNodeData as Action);
      } else if (node.type === 'trigger') {
        workflowStore.updateTrigger(node.name, updatedNodeData as Trigger);
      } else if (node.type === 'tool' && node.parentAgentName && node.toolName && node.innerActionName) {
        // For tools, update the inner action within the tool
        workflowStore.updateToolInnerAction(
          node.parentAgentName,
          node.toolName,
          node.innerActionName,
          updatedNodeData as Action
        );
        
        // Also update tool metadata (description, schema) if changed
        const toolDescription = parameterValues['_toolDescription'] as string | undefined;
        const agentParameterSchema = parameterValues['_agentParameterSchema'] as Record<string, unknown> | null | undefined;
        
        // Build tool update object with changed fields
        const toolUpdate: Record<string, unknown> = {};
        if (toolDescription !== undefined) {
          toolUpdate.description = toolDescription;
        }
        if (agentParameterSchema !== undefined) {
          toolUpdate.agentParameterSchema = agentParameterSchema;
        }
        
        // Only call updateTool if there are changes
        if (Object.keys(toolUpdate).length > 0) {
          workflowStore.updateTool(node.parentAgentName, node.toolName, toolUpdate);
        }
        
        // Handle tool name change (requires renaming the tool key)
        const newToolName = parameterValues['_toolName'] as string | undefined;
        if (newToolName && newToolName !== node.toolName) {
          workflowStore.renameTool(node.parentAgentName, node.toolName, newToolName);
        }
      }

      // Clone settings before set() to avoid Immer proxy issues with structuredClone
      const clonedSettings = settingsValues ? structuredClone(settingsValues) : null;

      set((state) => {
        state.originalValues = { ...state.parameterValues };
        state.originalSettings = clonedSettings;
        state.isDirty = false;
      });

      return { success: true };
    },

    setNodeName: (name: string) => {
      if (get().isReadOnly) return;
      set((state) => {
        if (state.node) {
          const prefix = state.node.type === 'trigger' ? 'trigger-' : 'action-';
          state.node = {
            ...state.node,
            name,
            id: prefix + name,
          };
        }
      });
    },

    setInnerActionName: (innerActionName: string) => {
      if (get().isReadOnly) return;
      set((state) => {
        if (state.node && state.node.type === 'tool') {
          state.node = {
            ...state.node,
            innerActionName,
          };
        }
      });
    },

    discardChanges: () => {
      const { originalSettings, originalValues } = get();
      set((state) => {
        // Reset parameter values
        state.parameterValues = { ...originalValues };
        // Deep clone settings to prevent shared references
        // Must read originalSettings from get() to avoid immer proxy issues
        state.settingsValues = originalSettings ? deepClone(originalSettings) : null;
        state.isDirty = false;
        state.validationErrors = {};
        state.settingsValidationErrors = {};
      });
    },
  }))
);
